<script>
  // @ts-check
  import { createEventDispatcher } from "svelte";
  import { cubicInOut } from "svelte/easing";
  import { fade } from "svelte/transition";

  import GridContainer from "../../atoms/GridContainer.svelte";
  import Logo from "../../atoms/Logo.svelte";
  import { getTheme } from "../../utilities/themes";
  // @ts-ignore
  // eslint-disable-next-line no-unused-vars
  import * as Types from "./globalNavigationTypes";
  import Constants from "./globalNavigationConstants";
  import Icon from "../../atoms/Icon.svelte";

  /** Primarily used for testing @type {string} */
  export let id = undefined;

  /**
   * The theme of the hero section
   * @type {import('../../utilities/themes.js').ThemeName}
   */
  export let theme = "white";

  /** @type {import('../../utilities/themes').Theme}*/
  let currentTheme;

  /** @type {string}  Choose which one is the active route*/
  export let currentRoute;

  /** @type {Types.MainNavigation} */
  export let mainNavigation;

  /** @type {string} */
  export let mainLogoAccessibilityText;

  /** @type {string} */
  export let displayedSection;

  /** @type {boolean} */
  export let isSearchPanelVisible;

  /** @type {boolean} */
  export let hasScrolledPastNav;

  export const dispatch = createEventDispatcher();

  /** @type {boolean} */
  export let delayThemeFadeOnNavBar = false;

  /** @type {boolean} */
  export let animateNavBarItems = false;

  $: currentTheme = getTheme(theme);
  $: componentColorTheme = currentTheme.componentColorTheme;
  $: isOpen = isSearchPanelVisible || !!displayedSection;

  $: transitionClass = animateNavBarItems ? "transition-all duration-[150ms] ease-nav-easing" : "";

  $: transitionDelayClass =
    isOpen || delayThemeFadeOnNavBar || hasScrolledPastNav || !animateNavBarItems
      ? ""
      : "delay-[650ms]";

  $: logoClass = `absolute bottom-1/2 left-0 top-1/2 h-full w-full -translate-y-1/2 ${transitionDelayClass} ${transitionClass}`;
  $: logoOpacityClass = componentColorTheme === "white" ? "opacity-[1]" : "opacity-[0]";
  $: logoDarkOpacityClass = componentColorTheme === "white" ? "opacity-[0]" : "opacity-[1]";

  const handleSearchClose = () => {
    dispatch("searchClose");
  };

  const currentRouteClasses = `
    after:w-1 after:h-1
    after:absolute after:bg-current
    after:bottom-[-0.5rem]
    after:left-[50%]
    after:translate-x-[-50%]
  `;

  $: linksTransitionClass = isSearchPanelVisible
    ? "opacity-[0] delay-[50ms]"
    : "opacity-[1] delay-[650ms]";
</script>

<GridContainer containerClass="relative z-[--stack-level-global-nav]">
  <div
    {id}
    class="{currentTheme.textColor} override-nav flex h-nav-height-desktop items-center justify-between {transitionClass} {transitionDelayClass}"
  >
    <ul class="flex flex-1 items-center gap-5">
      <li class="contents">
        <a
          data-sveltekit-reload
          href="/"
          data-transition-focus={Constants.transitionFocus.logo}
          class="{currentTheme.focusOutlineColor} relative mr-5 h-[44px] w-[44px] hover:text-cobalt-blue focus-visible:outline-dashed focus-visible:outline-offset-2"
        >
          <span class="sr-only">{mainLogoAccessibilityText}</span>
          <Logo type="obamaFoundationIcon" class="{logoClass} {logoOpacityClass}" />
          <Logo type="obamaFoundationIconDarkMode" class="{logoClass} {logoDarkOpacityClass}" />
        </a>
      </li>
      {#if mainNavigation?.length > 0}
        {#each mainNavigation as primarySection (primarySection.link.label)}
          {@const isCurrentRoute = primarySection?.link?.url === currentRoute}
          {@const isDisplayingSecondaryMenu = primarySection?.link?.label === displayedSection}
          {@const primaryLinkLabel = primarySection?.link?.label.replace(/\s/g, "")}
          <li
            class="flex items-center gap-1 transition-opacity duration-[150ms] ease-nav-easing {linksTransitionClass} {isSearchPanelVisible
              ? 'pointer-events-none'
              : ''}"
            on:mouseenter={() => {
              dispatch("hoveredLabel", {
                displayedSection: primarySection?.link?.label
              });
            }}
          >
            <a
              aria-hidden={isSearchPanelVisible}
              tabindex={isSearchPanelVisible ? -1 : 0}
              data-sveltekit-reload
              id={Constants.primaryLinkPrefix + primaryLinkLabel}
              class="relative flex items-center focus-visible:outline-dashed focus-visible:outline-offset-2 {currentTheme.hoverTextColor} {currentTheme.focusOutlineColor} {isCurrentRoute
                ? currentRouteClasses
                : ''}
                "
              href={primarySection?.link?.url}
            >
              {primarySection?.link?.label}
            </a>
            <button
              aria-hidden={isSearchPanelVisible}
              tabindex={isSearchPanelVisible ? -1 : 0}
              aria-controls={Constants.secondarySectionPrefix + primaryLinkLabel}
              aria-expanded={primarySection?.link?.label === displayedSection}
              aria-label={`${primarySection?.link?.label} Menu`}
              on:click={() => {
                if (isDisplayingSecondaryMenu) {
                  dispatch("hoveredLabel", {
                    displayedSection: undefined
                  });
                } else {
                  dispatch("hoveredLabel", {
                    displayedSection: primarySection?.link?.label
                  });
                }
              }}
              class="
                pointer-events-none h-[2.25rem]
                opacity-[0] focus-visible:pointer-events-auto focus-visible:opacity-[100]
                {currentTheme.focusOutlineColor} {isDisplayingSecondaryMenu
                ? 'text-cobalt-blue'
                : currentTheme.textColor}
                focus-visible:outline-dashed focus-visible:outline-offset-2
              "
            >
              <Icon name={isDisplayingSecondaryMenu ? "caretUp" : "caretDown"} />
            </button>
          </li>
        {/each}
      {/if}
    </ul>

    {#if isSearchPanelVisible}
      <button
        on:click={handleSearchClose}
        aria-label="Close Search Panel"
        aria-expanded={isSearchPanelVisible}
        in:fade={{ duration: 300, delay: 450, easing: cubicInOut }}
        class="relative flex h-[24px] w-[24px] items-center justify-center focus-visible:outline-dashed focus-visible:outline-offset-2"
      >
        <Icon name="close" />
      </button>
    {:else}
      <div
        in:fade={{ duration: 150, delay: 450, easing: cubicInOut }}
        class="flex items-center gap-6"
      >
        <slot name="utilityNavigation" />
      </div>
    {/if}
  </div>
</GridContainer>
